import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Modal,
  Button,
  Space,
  Select,
  Form,
  Typography,
  Pagination,
  Spin,
} from "antd";
import axiosInstance from "../../api/axiosInstance";
import { FundViewOutlined, ReloadOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import dayjs from "dayjs";
import { FilterRequest, OrderTypes, OrderValue } from "../../utils/Filters/FilterModels";
import FilterHelper from "../../utils/Filters/FilterHelper";

const { Option } = Select;
const { Title } = Typography;

interface AuditLog {
  id: number;
  featureName: string;
  action: string;
  changedBy?: string;
  changedAt: string;
  description: string;
  changedColumns?: string[];
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
}

const actionColors: Record<string, string> = {
  'Create': "green",
  'Update': "blue",
  'Delete': "red",
};

const AuditLogManagement: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [form] = Form.useForm();
  const [filter, setFilter] = useState<FilterRequest>(new FilterRequest({
    page: 1,
    limit: 10,
    keyword: '',
    summary: '',
    filters: [],
    orders: [new OrderValue({ type: OrderTypes.Desc, propertyName: 'createdDate', index: 0 })],
    isFull: false,
    tree: false,
  }));
  const fetchLogs = async (paramValues?: any) => {
    setLoading(true);
    const values = form.getFieldsValue();
    const res = await axiosInstance.get(`/activity-log`, {
      params: paramValues,

    });

    setLogs(res.data.list);
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  // const handleTableChange = (pagination: any) => {
  //   fetchLogs(pagination.current, pagination.pageSize);
  // };

  // const handleSearch = () => {
  //   fetchLogs(1, pagination.pageSize);
  // };

  const handleReLoad = () => {
    fetchLogs();
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    const newFilter = new FilterRequest({
      ...filter,
      page,
      orders: [new OrderValue({ type: OrderTypes.Desc, propertyName: 'createdDate', index: 0 })],
      limit: pageSize,
    });
    setFilter(newFilter);
    const params = FilterHelper.getParamWithFilter(newFilter);
    fetchLogs(params);
  };
  const fieldNameMap: Record<string, string> = {
    FullName: "Họ và tên",
    PhoneNumber: "Số điện thoại",
    Email: "Email",
    UserName: "Tên đăng nhập",
    Address: "Địa chỉ",
    NickName: "NickName",
    UserCode: "Mã người dùng",
    Point: "Điểm đơn",
    DoublePoint: "Điểm đôi",
    AccumulatedPoint: "Tổng điểm",
    PointType: "Loại điểm",
    Gender: "Giới tính",
    DateOfBirth: "Ngày sinh",
    IsActive: "Trạng thái hoạt động",
    AvatarUser: "Ảnh đại diện",
    UpdatePointDate: "Ngày cập nhật điểm",
    AccountType: "Loại tài khoản",
    Guarantor: "Người bảo lãnh",
    Note: "Ghi chú",
    RacketUse: "Thông tin vợt",
    ShoesUse: "Thông tin giày",
    FavoriteBall: "Thông tin bóng"
  };

  const formatValue = (key: string, value: any): string => {
    if (key === "Gender") {
      return value === 0 ? "Nam" : value === 1 ? "Nữ" : "";
    }
    if (key === "DateOfBirth") {
      return value == null ? '' : dayjs(value).format("DD/MM/YYYY").toString() || '';
    }
    if (key === "UpdatePointDate") {
      return value == null ? '' : dayjs(value).format("DD/MM/YYYY").toString() || '';
    }
    if (key === "IsActive") {
      return value === true ? "Đang hoạt động" : value === false ? "Không hoạt động" : "";
    }
    if (value === null || value === undefined) {
      return "";
    }
    return String(value);
  };
  const renderDiffTable = (oldVals: any, newVals: any) => {
    const allKeys = Array.from(new Set([
      ...Object.keys(oldVals || {}),
      ...Object.keys(newVals || {})
    ]));

    return (
      <Table
        dataSource={allKeys.map((key) => ({
          key,
          field: fieldNameMap[key] || key,
          oldValue: formatValue(key, oldVals?.[key]),
          newValue: formatValue(key, newVals?.[key])
        }))}
        columns={[
          { title: "Thông tin thay đổi", dataIndex: "field", width: "30%" },
          { title: "Giá trị cũ", dataIndex: "oldValue", width: "35%" },
          { title: "Giá trị mới", dataIndex: "newValue", width: "35%" }
        ]}
        pagination={false}
        size="small"
        bordered
      />
    );
  };

  const columns = [
    { title: "activityLogID", dataIndex: "activityLogID", key: "ativityLogID", hidden: true },

    { title: "Chức năng", dataIndex: "featureName" },
    {
      title: "Hành động",
      dataIndex: "eventAction",
      render: (action: string) => (
        <Tag color={actionColors[action] || "default"}>{action}</Tag>
      ),
    },
    { title: "Người thực hiện", dataIndex: "changedName" },
    { title: "Mô tả", dataIndex: "description" },
    {
      title: "Thời gian thực hiện",
      dataIndex: "changedAt",
      render: (val: string) => new Date(val).toLocaleString(),
    },
    {
      title: "Chi tiết",
      render: (_: any, record: AuditLog) => (
        <Space>
          <Button icon={<FundViewOutlined />} danger onClick={() => setSelectedLog(record)}>
            Xem
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Content style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Space style={{ width: "100%" }}>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleReLoad}
        >
          Làm mới
        </Button>
      </Space>
      <div style={{ flex: 1, overflow: "auto", marginTop: "16px" }}>
        {loading ? (
          <Spin fullscreen tip="Đang tải dữ liệu..." size="large" />
        ) : (
          <Table
            // rowKey="id"
            columns={columns}
            dataSource={logs}
            pagination={false}
            scroll={{ y: 60 * 10 }}
            onRow={(record) => ({
              onClick: (e) => {
                const actionColumnIndex = columns.findIndex((col) => col.key === "action");
                const clickedColumn = (e.target as HTMLElement).closest("td");

                if (clickedColumn && clickedColumn.cellIndex === actionColumnIndex) {
                  return;
                }

                setSelectedLog(record);
              },
            })}
          />
        )}
      </div>

      <div
        style={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#fff",
          paddingTop: "10px",
          borderTop: "-1px solid #ddd",
          boxShadow: "0 -2px 4px rgba(0,0,0,0.1)",
          textAlign: "center",
        }}
      >
        <Pagination
          current={filter.page}
          pageSize={filter.limit}
          // total={totalRecords}
          onChange={handlePaginationChange}
          showTotal={(total, range) =>
            `Hiển thị ${range[0]} - ${range[1]} của ${total} bản ghi`
          } />
      </div>

      <Modal
        open={!!selectedLog}
        title={selectedLog?.description}
        onCancel={() => setSelectedLog(null)}
        footer={null}
        width={800}
      >
        {renderDiffTable(selectedLog?.oldValues, selectedLog?.newValues)}
      </Modal>
    </Content>
  );
};

export default AuditLogManagement;
